import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { supabaseReportException } from "@/lib/logs/reportExceptions";

export interface UserProfile {
  user_id: string;
  display_name: string | null;
  username: string | null;
  avatar_url: string | null;
  city: string | null;
  specialties: string[] | null;
  phone: string | null;
  phone_verified: boolean | null;
  user_type: string | null;
}

export const useUserProfile = (userId: string | undefined) => {
  return useQuery({
    queryKey: ["user-profile", userId],
    queryFn: async () => {
      if (!userId) throw new Error("User ID is required");

      const { data, error } = await supabase
        .from("profiles")
        .select("user_id, display_name, user_type, username, avatar_url, city, specialties, phone, phone_verified")
        .eq("user_id", userId)
        .single();

      if (error) throw error;

      return data as UserProfile;
    },
    enabled: !!userId,
    staleTime: 10 * 60 * 1000, // 10 dakika fresh tut (profil bilgileri çok nadir değişir)
    placeholderData: (previousData) => previousData, // Loading state'i önle
  });
};

export const useUpdateUserProfile = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (updates: { city?: string | null; specialties?: string[] | null }) => {
      if (!user?.id) throw new Error("User not authenticated");

      const { error } = await supabase
        .from("profiles")
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq("user_id", user.id);

      if (error) {
        supabaseReportException(error, {
          operation: "useUpdateUserProfile",
          additionalData: { userId: user.id },
        });
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user-profile", user?.id] });
    },
  });
};

// Utility function to format name as "Name + First Letter of Surname."
export const formatDisplayName = (profile: UserProfile | null | undefined): string => {
  if (!profile) return "İşveren";

  const fullName = profile.display_name || profile.username;
  if (!fullName) return "İşveren";

  const nameParts = fullName.trim().split(/\s+/);
  if (nameParts.length < 2) {
    return nameParts[0]; // Only first name available
  }

  const firstName = nameParts[0];
  const lastNameFirstLetter = nameParts[nameParts.length - 1].charAt(0).toUpperCase();

  return `${firstName} ${lastNameFirstLetter}.`;
};

// Utility function to get avatar initials
export const getAvatarInitials = (profile: UserProfile | null | undefined): string => {
  if (!profile) return "İ";

  const fullName = profile.display_name || profile.username;
  if (!fullName) return "İ";

  const nameParts = fullName.trim().split(/\s+/);
  if (nameParts.length === 1) {
    return nameParts[0].charAt(0).toUpperCase();
  }

  const firstInitial = nameParts[0].charAt(0).toUpperCase();
  const lastInitial = nameParts[nameParts.length - 1].charAt(0).toUpperCase();

  return `${firstInitial}${lastInitial}`;
};

